import React, { useEffect, useRef, useState } from 'react';
import { BottomSheet } from 'react-spring-bottom-sheet';
import classNames from 'classnames';
import { useSendMessageMutation } from '../../../store/chat/chatApi';
import { EMessageSender, IAiChatMessage } from '../../../proto/aiChat/aiChat';
import './aiWaiterChat.scss';

interface IWaiterChatProps {
	onDismiss: () => void;
	isOpen: boolean;
}

export const AiWaiterChat = ({ onDismiss, isOpen }: IWaiterChatProps): React.JSX.Element => {
	const [messages, setMessages] = useState<IAiChatMessage[]>([]);
	const [input, setInput] = useState('');
	const listRef = useRef<HTMLDivElement | null>(null);
	const [open, setOpen] = useState(false);

	const [sendMessage, { isLoading }] = useSendMessageMutation();

	useEffect(() => {
		setOpen(isOpen);
	}, [isOpen]);

	useEffect(() => {
		listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
	}, [messages, isLoading]);

	async function onSubmit(e: React.FormEvent): Promise<void> {
		e.preventDefault();
		const text = input.trim();

		if (!text || isLoading) {
			return;
		}

		const userMsg: IAiChatMessage = { sender: EMessageSender.User, content: text };
		const optimistic = [...messages, userMsg];

		setMessages(optimistic);
		setInput('');

		try {
			const res = await sendMessage({ message: userMsg, messagesHistory: messages }).unwrap();

			setMessages((prev) => [...prev, res]);
		} catch (err: any) {
			setMessages((prev) => [...prev, { sender: EMessageSender.AI, content: `Error: ${err?.message ?? 'server unreachable'}` }]);
		}
	}

	const onDismissSheet = (): void => {
		setOpen(false);
		onDismiss();
	};

	return (
		<BottomSheet
			open={open}
			onDismiss={onDismissSheet}
			defaultSnap={({ snapPoints, lastSnap }): any => lastSnap ?? Math.min(...snapPoints)}
			snapPoints={({ maxHeight }): any => [maxHeight - maxHeight / 5, maxHeight]}
			header={<div>עגלת קניות</div>}
			className="bottom-sheet-container"
			expandOnContentDrag={false}
		>
			<div className="aiChatContainer">
				<div className="aiChatHeader">Chat</div>

				<div ref={listRef} className="aiChatMessagesList">
					{messages.map((m) => (
						<div
							key={m.id}
							className={classNames('aiChatMessage', m.sender === EMessageSender.User ? 'aiChatMessageUser' : 'aiChatMessageAssistant')}
						>
							{m.content}
						</div>
					))}
					{isLoading && (
						<div className="aiChatMessage aiChatMessageAssistant">
							<span className="aiChatMessageLoadingIndicator">
								<span className="animate-pulse">•</span>
								<span className="animate-pulse" style={{ animationDelay: '150ms' }}>
									•
								</span>
								<span className="animate-pulse" style={{ animationDelay: '300ms' }}>
									•
								</span>
							</span>
						</div>
					)}
				</div>

				<form onSubmit={onSubmit} className="aiChatInputForm">
					<textarea
						value={input}
						onChange={(e): void => setInput(e.target.value)}
						placeholder="Type your message..."
						onKeyDown={(e): void => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								onSubmit(e as any);
							}
						}}
					/>
					<button type="submit" disabled={!input.trim() || isLoading}>
						Send
					</button>
				</form>
			</div>
		</BottomSheet>
	);
};
